import React from 'react'

import { RHFInput } from 'react-hook-form-input'
import useForm from 'react-hook-form'

import {
  Button,
  Divider,
  TextField,
  Tooltip,
  IconButton,
  makeStyles,
} from '@material-ui/core'

import Alert from '@material-ui/lab/Alert'

import { Delete as DeleteIcon } from '@material-ui/icons'
import { KeyboardDatePicker } from '@material-ui/pickers'

import { InputAutocomplete, InputCurrency } from 'components/Form'
import useCollapse from 'hooks/useCollapse'
import * as service from 'service'
import moment from 'moment'
import schemas from './../../schemas'
import styles from './styles'

const useStyles = makeStyles(styles)

const CollapseBody = ({ item }) => {
  const classes = useStyles()
  const collapse = useCollapse()

  const {
    register,
    setValue,
    errors,
    watch,
    handleSubmit,
    unregister,
  } = useForm({
    defaultValues: {
      ...item,
      itemType: item.itemType || 'planType',
      typeEntry: item.typeEntry || 'debit',
    },
    validationSchema: schemas.collapse,
  })

  const onSubmit = async invoiceItem => {
    const data = {
      ...invoiceItem,
      collapseSettings: { expand: false, edit: false },
    }

    collapse.update(item.collapseId, data)
  }

  const handleClickEditButton = event => {
    event.preventDefault()

    collapse.toggleEdit(item.collapseId)
  }

  const values = watch()

  React.useEffect(() => {
    if (values.itemType === 'planType') {
      register({ name: 'from' })
      register({ name: 'till' })
    }
  }, [register, values.itemType])

  const handleClickCancelButton = event => {
    event.preventDefault()

    const prevItem = collapse.get(item.collapseId)

    setValue('plan', prevItem.plan, true)
    setValue('description', prevItem.description, true)
    setValue('from', prevItem.from, true)
    setValue('till', prevItem.till, true)
    setValue('price', prevItem.price, true)
    setValue('typeEntry', prevItem.entryType, true)
    setValue('itemType', prevItem.itemType, true)

    collapse.toggleEdit(item.collapseId, true)
  }

  const handleChangeType = event => {
    const type = event.target.value
    unregister('from')
    unregister('till')
    unregister('plan')
    unregister('price')
    unregister('description')
    unregister('typeEntry')
    if (type === 'entryType') {
      register({ name: 'typeEntry' })
    }
  }

  const handleChangePlan = event => {
    const plan = event.target.value
    if (!plan) {
      setValue('from', null)
      setValue('till', null)
      return
    }

    setValue('price', plan.price, true)
  }

  const handleClickRemoveButton = () => {
    collapse.remove(item.collapseId)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={classes.root}>
      <div className={classes.container}>
        <div className={classes.rowInput}>
          <RHFInput
            as={
              <TextField
                label="Tipo do item"
                className={`${classes.input} ${classes.inputHalf}`}
                SelectProps={{ native: true }}
                size="small"
                margin="dense"
                select
                disabled={!item.collapseSettings.edit}
              >
                <option value="planType">Plano</option>
                <option value="entryType">Avulso</option>
              </TextField>
            }
            name="itemType"
            register={register}
            setValue={setValue}
            onChange={handleChangeType}
            mode="onChange"
          />
        </div>
        {values.itemType === 'planType' ? (
          <>
            <div className={classes.rowInput}>
              <RHFInput
                as={
                  <InputAutocomplete
                    fn={service.recurrence.plans.get}
                    fnLabel={option => option.name || ''}
                    entity="plans"
                    keyEntity="id"
                    searchParam="plan[name]"
                    fixedFilter={{ perPage: 10 }}
                    textFieldProps={{
                      label: 'Selecione o plano',
                      margin: 'dense',
                      variant: 'outlined',
                      fullWidth: true,
                      error: !!errors.plan,
                      helperText: errors && errors.plan && errors.plan.message,
                    }}
                    className={`${classes.input} ${classes.inputHalf}`}
                    size="small"
                    disabled={!item.collapseSettings.edit}
                  />
                }
                name="plan"
                register={register}
                setValue={setValue}
                onChange={handleChangePlan}
                mode="onChange"
              />
              <RHFInput
                as={
                  <InputCurrency
                    label="Valor"
                    margin="dense"
                    fullWidth
                    className={`${classes.input} ${classes.inputHalf}`}
                    error={!!errors.price}
                    helperText={errors && errors.price && errors.price.message}
                    disabled={!item.collapseSettings.edit}
                  />
                }
                name="price"
                register={register}
                setValue={setValue}
                mode="onChange"
              />
            </div>
            <div className={classes.rowInput}>
              <KeyboardDatePicker
                margin="dense"
                placeholder="Data de início da vigência"
                format="dd/MM/yyyy"
                name="from"
                className={`${classes.input} ${classes.inputHalf}`}
                error={errors.from !== undefined}
                helperText={errors.from ? errors.from.message : ''}
                onChange={element =>
                  setValue(
                    'from',
                    element &&
                      moment(element)
                        .utc(false)
                        .format(),
                  )
                }
                value={values.from || null}
                disabled={!item.collapseSettings.edit}
              ></KeyboardDatePicker>
              <KeyboardDatePicker
                margin="dense"
                placeholder="Data de fim da vigência"
                format="dd/MM/yyyy"
                name="till"
                className={`${classes.input} ${classes.inputHalf}`}
                error={errors.till !== undefined}
                helperText={errors.till ? errors.till.message : ''}
                onChange={element =>
                  setValue(
                    'till',
                    element &&
                      moment(element)
                        .utc(false)
                        .format(),
                  )
                }
                value={values.till || null}
                disabled={!item.collapseSettings.edit}
              ></KeyboardDatePicker>
            </div>
            <div className={classes.info}>
              <Alert severity="info">
                A vigência informada é apenas um informativo para esta fatura.
              </Alert>
            </div>
          </>
        ) : (
          <>
            <div className={classes.rowInput}>
              <RHFInput
                as={
                  <TextField
                    label="Tipo do avulso"
                    className={`${classes.input} ${classes.inputHalf}`}
                    SelectProps={{ native: true }}
                    disabled={!item.collapseSettings.edit}
                    select
                    margin="dense"
                    size="small"
                  >
                    <option value="debit">Acréscimo</option>
                    <option value="discount">Desconto</option>
                  </TextField>
                }
                name="typeEntry"
                register={register}
                setValue={setValue}
                mode="onChange"
              />
            </div>
            <div className={classes.rowInput}>
              <RHFInput
                as={
                  <TextField
                    label="Descrição"
                    type="text"
                    margin="dense"
                    fullWidth
                    error={!!errors.description}
                    helperText={
                      errors && errors.description && errors.description.message
                    }
                    className={`${classes.input} ${classes.inputHalf}`}
                    disabled={!item.collapseSettings.edit}
                  />
                }
                name="description"
                register={register}
                setValue={setValue}
                mode="onChange"
              />
              <RHFInput
                as={
                  <InputCurrency
                    label="Valor"
                    margin="dense"
                    fullWidth
                    className={`${classes.input} ${classes.inputHalf}`}
                    error={!!errors.price}
                    helperText={errors && errors.price && errors.price.message}
                    disabled={!item.collapseSettings.edit}
                  />
                }
                name="price"
                register={register}
                setValue={setValue}
                mode="onChange"
              />
            </div>
          </>
        )}
      </div>

      <Divider />

      <div className={classes.buttonGroup}>
        <div>
          {item.collapseSettings.edit ? (
            <>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                className={classes.buttonSave}
              >
                Salvar
              </Button>
              {item.collapseSettings.updatedOnce && (
                <Button
                  variant="outlined"
                  color="primary"
                  type="button"
                  className={classes.buttonSave}
                  onClick={handleClickCancelButton}
                >
                  Cancelar
                </Button>
              )}
            </>
          ) : (
            <Button
              variant="contained"
              color="primary"
              type="button"
              onClick={handleClickEditButton}
            >
              Editar
            </Button>
          )}
        </div>

        {collapse.data.length > 1 && (
          <Tooltip title="Remover">
            <IconButton aria-label="Remover" onClick={handleClickRemoveButton}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        )}
      </div>
    </form>
  )
}

export default CollapseBody
